import { useFormikEnhanced } from '@superdispatch/forms';
import { Column, Columns, useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { FormikProvider } from 'formik';
import { useMemo } from 'react';
import {
  setDriverPreferencesTransportType,
  useDriverPreferences,
  useDriverPreferencesCacheUpdater,
} from 'shared/modules/driver/DriverPreferencesAPI';
import {
  DriverPreferencesTransportTypeDTO,
  driverPreferencesTransportTypeSchema,
  prepareTransportTypePreferences,
} from 'shared/modules/driver/DriverPreferencesDTO';
import { trackDriverPreferences } from '../DriverPreferencesAnalytics';
import {
  DriverPreferencesStepItem,
  useDriverPreferencesStepperContext,
} from '../DriverPreferencesStepper';
import { TransportTypeStepContent } from './TransportTypeStepContent';

export function TransportTypeStep() {
  const { handleNext, handlePrevious } = useDriverPreferencesStepperContext();
  const { data } = useDriverPreferences();
  const { addSnackbar } = useSnackbarStack();

  const initialValues = useMemo(
    () => driverPreferencesTransportTypeSchema.cast(data),
    [data],
  );

  const updateCache = useDriverPreferencesCacheUpdater();

  const formik = useFormikEnhanced<DriverPreferencesTransportTypeDTO, unknown>({
    initialValues,
    validationSchema: driverPreferencesTransportTypeSchema,
    onSubmit: (values) => {
      return setDriverPreferencesTransportType(
        prepareTransportTypePreferences(values),
      );
    },
    onSubmitSuccess: (_, values) => {
      trackDriverPreferences({
        name: 'Driver Completed Step 2 Transport Type',
      });
      updateCache((cache) => {
        return { ...cache, ...values };
      });
      handleNext();
    },
    onSubmitFailure(error) {
      addSnackbar(error.message || 'Failed to save preferences', {
        variant: 'error',
        hasCloseButton: true,
      });
    },
  });
  const { isSubmitting } = formik;

  return (
    <FormikProvider value={formik}>
      <DriverPreferencesStepItem
        actions={
          <Columns space="small">
            <Column width="1/2">
              <Button
                fullWidth={true}
                variant="neutral"
                onClick={handlePrevious}
              >
                Back
              </Button>
            </Column>
            <Column width="1/2">
              <Button
                type="submit"
                pending={isSubmitting}
                fullWidth={true}
                onClick={() => {
                  void formik.submitForm();
                }}
              >
                Continue
              </Button>
            </Column>
          </Columns>
        }
      >
        <TransportTypeStepContent />
      </DriverPreferencesStepItem>
    </FormikProvider>
  );
}
